// components/DailyReport/DailyReportSearchModal.tsx
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Grid,
    Box,
    IconButton,
    InputAdornment,
    Typography,
    DialogContentText,
    Autocomplete
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import { DailyReportSearchParams } from '../../types/dailyReport';
import { Member } from '../../types/member';
import { buildMemberShortName } from '../../utils/memberDisplay';
import { Customer } from '../../types/customer';

// ローカルストレージのキー
const STORAGE_KEY = 'dailyReportSearchParams';

interface DailyReportSearchModalProps {
    open: boolean;
    onClose: () => void;
    onSearch: (params: DailyReportSearchParams) => void;
    members: Member[];
    customers: Customer[];
}

const DailyReportSearchModal: React.FC<DailyReportSearchModalProps> = ({
    open,
    onClose,
    onSearch,
    members,
    customers
}) => {
    // 検索条件の初期化をローカルストレージから行う
    const [searchParams, setSearchParams] = useState<DailyReportSearchParams>(() => {
        try {
            const savedParams = localStorage.getItem(STORAGE_KEY);
            if (savedParams) {
                return JSON.parse(savedParams);
            }
        } catch (error) {
            console.error('Failed to parse search params from localStorage:', error);
        }
        // デフォルト値
        return {
            constructionQuotationNo: '',
            customerName: '',
            siteName: '',
            constructionDetails: '',
            supervisor: '',
            reportDate: ''
        };
    });

    const [dialogType, setDialogType] = useState<'supervisor' | null>(null);

    // ローカルストレージから顧客情報を復元
    const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(() => {
        if (searchParams.customerName) {
            // 顧客名で顧客を検索
            return customers.find(customer => customer.Customer_Name === searchParams.customerName) || null;
        }
        return null;
    });

    // モーダルが開いたときに最新の保存された検索条件を取得
    useEffect(() => {
        if (open) {
            try {
                const savedParams = localStorage.getItem(STORAGE_KEY);
                if (savedParams) {
                    const parsedParams = JSON.parse(savedParams);

                    // 明示的に全てのプロパティを設定
                    setSearchParams({
                        constructionQuotationNo: parsedParams.constructionQuotationNo || '',
                        customerName: parsedParams.customerName || '',
                        siteName: parsedParams.siteName || '',
                        constructionDetails: parsedParams.constructionDetails || '',
                        supervisor: parsedParams.supervisor || '',
                        reportDate: parsedParams.reportDate || ''
                    });

                    if (parsedParams.customerName) {
                        const foundCustomer = customers.find(
                            customer => customer.Customer_Name === parsedParams.customerName
                        );
                        setSelectedCustomer(foundCustomer || null);
                    } else {
                        setSelectedCustomer(null);
                    }
                }
            } catch (error) {
                console.error('Failed to retrieve search params from localStorage:', error);
            }
        }
    }, [open, customers]);

    // 検索パラメータの変更ハンドラ
    const handleChange = (name: keyof DailyReportSearchParams, value: string) => {
        setSearchParams(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // 検索実行
    const handleSubmit = () => {
        // カスタマーIDを使用
        const params = { ...searchParams };
        if (selectedCustomer) {
            params.customerName = selectedCustomer.Customer_Name;
        }

        // 日付のフォーマットをYYYY-MM-DD形式に統一（必要に応じて）
        if (params.reportDate) {
            const datePattern = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}$/;
            if (datePattern.test(params.reportDate)) {
                // スラッシュをハイフンに置換して統一
                params.reportDate = params.reportDate.replace(/\//g, '-');
                
                // 必要に応じて日付形式を調整（例：MM-DDを2桁に）
                const dateParts = params.reportDate.split('-');
                if (dateParts.length === 3) {
                    // YYYY-MM-DD形式に整形
                    params.reportDate = `${dateParts[0]}-${dateParts[1].padStart(2, '0')}-${dateParts[2].padStart(2, '0')}`;
                }
            }
        }

        // 検索条件をローカルストレージに保存
        localStorage.setItem(STORAGE_KEY, JSON.stringify(params));

        onSearch(params);
        onClose();
    };

    // パラメータリセット
    const handleReset = () => {
        const emptyParams = {
            constructionQuotationNo: '',
            customerName: '',
            siteName: '',
            constructionDetails: '',
            supervisor: '',
            reportDate: ''
        };
        setSearchParams(emptyParams);
        setSelectedCustomer(null);

        // ローカルストレージからも削除
        localStorage.removeItem(STORAGE_KEY);
    };

    // ソート済みのメンバー一覧
    const sortedMembers = [...members].sort((a, b) => (a.ID ?? 0) - (b.ID ?? 0));

    // モーダルが閉じられたときの処理
    useEffect(() => {
        if (!open) {
            setDialogType(null);
        }
    }, [open]);

    // 現在の日付を取得（DatePicker用）
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                fullWidth
                maxWidth="md"
                disableRestoreFocus
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
                    <Typography variant="h6" component="span">日報データ検索</Typography>
                    <IconButton edge="end" onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ py: 3 }}>
                    <Grid container spacing={3}>
                        {/* 工事番号 */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="工事番号"
                                value={searchParams.constructionQuotationNo || ''}
                                onChange={(e) => handleChange('constructionQuotationNo', e.target.value)}
                                placeholder="工事番号で検索"
                                InputProps={{
                                    endAdornment: searchParams.constructionQuotationNo ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleChange('constructionQuotationNo', '')}
                                                edge="end"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Grid>

                        {/* 顧客名 */}
                        <Grid item xs={12} md={6}>
                            <Autocomplete
                                value={selectedCustomer}
                                onChange={(_, newValue) => {
                                    setSelectedCustomer(newValue);
                                    // 選択された顧客の名前をセット
                                    handleChange('customerName', newValue?.Customer_Name || '');
                                }}
                                options={customers}
                                getOptionLabel={(option) => option.Customer_Name}
                                isOptionEqualToValue={(option, value) =>
                                    option.ID === value.ID
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="顧客名"
                                    />
                                )}
                                noOptionsText="該当する顧客がいません"
                            />
                        </Grid>

                        {/* 現場名 */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="現場名"
                                value={searchParams.siteName || ''}
                                onChange={(e) => handleChange('siteName', e.target.value)}
                                placeholder="現場名で検索"
                                InputProps={{
                                    endAdornment: searchParams.siteName ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleChange('siteName', '')}
                                                edge="end"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Grid>

                        {/* 工事内容 */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="工事内容"
                                value={searchParams.constructionDetails || ''}
                                onChange={(e) => handleChange('constructionDetails', e.target.value)}
                                placeholder="工事内容で検索"
                                InputProps={{
                                    endAdornment: searchParams.constructionDetails ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleChange('constructionDetails', '')}
                                                edge="end"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                            />
                        </Grid>

                        {/* 担当者 */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                label="担当者"
                                value={searchParams.supervisor || ''}
                                InputProps={{
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {searchParams.supervisor && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleChange('supervisor', '')}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                size="small"
                                                onClick={() => setDialogType('supervisor')}
                                            >
                                                <PersonSearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    )
                                }}
                                onClick={() => setDialogType('supervisor')}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'background.paper',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }
                                }}
                            />
                        </Grid>

                        {/* 日報日付（HTML5のtype="date"を使用） */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                type="date"
                                fullWidth
                                label="日報日付"
                                value={searchParams.reportDate || ''}
                                onChange={(e) => handleChange('reportDate', e.target.value)}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                InputProps={{
                                    endAdornment: searchParams.reportDate ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleChange('reportDate', '')}
                                                edge="end"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null
                                }}
                                // 未来の日付は不可
                                inputProps={{
                                    max: today
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        bgcolor: 'background.paper',
                                        '&:hover': {
                                            bgcolor: 'action.hover'
                                        }
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>

                    {/* 検索条件の説明 */}
                    <DialogContentText sx={{ mt: 3, fontSize: '0.9rem', color: 'text.secondary' }}>
                        * 担当者は完全一致、工事番号・顧客名・現場名・工事内容は部分一致で検索します。
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={handleReset} color="inherit">
                        リセット
                    </Button>
                    <Box sx={{ flex: '1 1 auto' }} />
                    <Button onClick={onClose} color="inherit">
                        キャンセル
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        color="primary"
                        startIcon={<SearchIcon />}
                    >
                        検索
                    </Button>
                </DialogActions>
            </Dialog>

            {/* 担当者選択ダイアログ */}
            <Dialog
                open={dialogType === 'supervisor'}
                onClose={() => setDialogType(null)}
                fullWidth
                maxWidth="sm"
                disableRestoreFocus
            >
                <DialogTitle>
                    担当者選択
                </DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ mb: 2 }}>
                        <Button
                            variant="outlined"
                            color="secondary"
                            fullWidth
                            onClick={() => {
                                handleChange('supervisor', '');
                                setDialogType(null);
                            }}
                        >
                            選択解除
                        </Button>
                    </Box>
                    <Grid container spacing={1}>
                        {sortedMembers.map((member) => {
                            const supervisorName = buildMemberShortName(member, members);
                            const isSelected = searchParams.supervisor === supervisorName;
                            return (
                                <Grid item xs={4} key={`supervisor-${member.ID}`}>
                                    <Button
                                        fullWidth
                                        variant={isSelected ? "contained" : "outlined"}
                                        color={isSelected ? "success" : "primary"}
                                        onClick={() => {
                                            handleChange('supervisor', supervisorName);
                                            setDialogType(null);
                                        }}
                                        sx={{
                                            height: '60px',
                                            whiteSpace: 'normal',
                                            lineHeight: 1.2,
                                            textTransform: 'none',
                                            ...(isSelected && {
                                                fontWeight: 'bold',
                                                boxShadow: 1,
                                            }),
                                        }}
                                    >
                                        <Typography variant="body1" component="span">{supervisorName}</Typography>
                                    </Button>
                                </Grid>
                            );
                        })}
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ borderTop: 1, borderColor: 'divider', px: 3, py: 2 }}>
                    <Button
                        onClick={() => setDialogType(null)}
                        variant="outlined"
                    >
                        閉じる
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DailyReportSearchModal;